import { doc, getDoc, setDoc, query, where, getDocs, collection, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';

export const NotificationsSettings = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inBrowserEnabled, setInBrowserEnabled] = useState(true);
  const [appEnabled, setAppEnabled] = useState(true);

  const [error, setError] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [emailContent, setEmailContent] = useState(true);
  const [emailTitle, setEmailTitle] = useState('');

  const userDocRef = useRef(null);
  const unsubRef = useRef(null);

  const Switch = ({ id, checked, onChange, disabled }) => (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`toggle ${checked ? 'toggle-on' : 'toggle-off'} ${disabled ? 'toggle-disabled' : ''}`}
    >
      <span className="knob" />
      <span className="sr-only">{checked ? 'On' : 'Off'}</span>
      <span className="state-label">{checked ? 'On' : 'Off'}</span>
    </button>
  );

  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    userDocRef.current = null;

    const init = async () => {
      try {
        setLoading(true);
        setError('');
        setSavedAt(null);

        if (!user?.uid) {
          setError('You must be signed in to view notification settings.');
          setLoading(false);
          return;
        }

        const authUid = user.uid;
        const idDocRef = doc(db, 'users', authUid);
        const idDoc = await getDoc(idDocRef);

        if (idDoc.exists()) {
          if (idDoc.data()?.authUID !== authUid) {
            await setDoc(idDocRef, { authUID: authUid }, { merge: true });
          }
          userDocRef.current = idDocRef;
        } else {
          const q = query(collection(db, 'users'), where('authUID', '==', authUid));
          const snap = await getDocs(q);

          if (!snap.empty) {
            userDocRef.current = snap.docs[0].ref;
          } else {
            // NEW DEFAULT VALUES ADDED HERE
            await setDoc(
              idDocRef,
              {
                authUID: authUid,
                emailNotifications: true,
                inBrowserNotifications: true,
                appNotifications: true,
                emailContent: true
              },
              { merge: true }
            );
            userDocRef.current = idDocRef;
          }
        }

        // FIRESTORE → UI SYNC (UPDATED)
        unsubRef.current = onSnapshot(userDocRef.current, (ds) => {
          const data = ds.data() || {};

          setEmailEnabled(data.emailNotifications !== false);
          setIsPremium(!!data.isPremium);
          setEmailContent(data.emailContent !== false);
          setEmailTitle(data.emailTitle || '');

          // NEW FUNCTIONALITY
          setInBrowserEnabled(data.inBrowserNotifications !== false);
          setAppEnabled(data.appNotifications !== false);
        });
      } catch (e) {
        console.error(e);
        setError('Failed to load your settings.');
      } finally {
        setLoading(false);
      }
    };

    void init();

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [user]);

  const persistPreference = useCallback(
    async (preferences) => {
      if (!user?.uid || !userDocRef.current) return;
      setSaving(true);
      setSavedAt(null);
      setError('');

      try {
        await setDoc(
          userDocRef.current,
          {
            authUID: user.uid,
            ...preferences
          },
          { merge: true }
        );
        setSavedAt(new Date());
      } catch (e) {
        console.error(e);
        setError('Could not save your preference. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [user]
  );

  const handleEmailToggle = (val) => {
    setEmailEnabled(val);
    void persistPreference({ emailNotifications: val });
  };

  const handleInBrowserToggle = (val) => {
    setInBrowserEnabled(val);
    void persistPreference({ inBrowserNotifications: val });
  };

  const handleAppToggle = (val) => {
    setAppEnabled(val);
    void persistPreference({ appNotifications: val });
  };

  const handleEmailContentToggle = (val) => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    setEmailContent(val);
    void persistPreference({ emailContent: val });
  };

  const handleEmailTitleChange = (e) => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    setEmailTitle(e.target.value);
  };

  const handleEmailTitleBlur = () => {
    if (isPremium) {
      void persistPreference({ emailTitle: emailTitle });
    }
  };

  return (
    <div className="main settings notifications">
      <Modal
        isOpen={showUpgradeModal}
        onRequestClose={() => setShowUpgradeModal(false)}
        contentLabel="Premium Feature"
        className="upgrade-prompt"
      >
        <h2>Premium Feature</h2>
        <p>This is a premium feature. Please upgrade to use it.</p>
        <div className="actions">
          <button onClick={() => setShowUpgradeModal(false)}>Maybe Later</button>
          <button onClick={() => navigate('/pricing')}>Upgrade</button>
        </div>
      </Modal>

      <div>
        <h1>Notifications</h1>
        <p>Control how you receive alerts from the platform</p>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <div>
            {/* EMAIL NOTIFICATIONS */}
            <div className="notification-options">
              <h1>Email Notification</h1>

              <label htmlFor="email-toggle" className="notification-toggle">
                <span>Email notifications</span>
                <Switch id="email-toggle" checked={emailEnabled} onChange={handleEmailToggle} disabled={saving} />
              </label>

              <label htmlFor="email-content-toggle" className="notification-toggle">
                <span>Include message content in email</span>
                <Switch
                  id="email-content-toggle"
                  checked={emailContent}
                  onChange={handleEmailContentToggle}
                  disabled={saving || !isPremium}
                />
              </label>

              <div className="notification-toggle">
                <label htmlFor="email-title">Custom email title</label>
                <input
                  type="text"
                  id="email-title"
                  value={emailTitle}
                  onChange={handleEmailTitleChange}
                  onBlur={handleEmailTitleBlur}
                  disabled={saving || !isPremium}
                  placeholder="New message from..."
                />
              </div>
            </div>

            {/* IN-BROWSER NOTIFICATIONS */}
            <div className="notification-options">
              <h2 style={{ fontSize: '24px' }}>In-Browser Notification</h2>
              <label htmlFor="in-browser-toggle" className="notification-toggle">
                <span>Enable In-Browser Notification</span>
                <Switch
                  id="in-browser-toggle"
                  checked={inBrowserEnabled}
                  onChange={handleInBrowserToggle}
                  disabled={saving}
                />
              </label>
            </div>

            {/* APP NOTIFICATIONS */}
            {/* <div className="notification-options">
              <h2 style={{ fontSize: '24px' }}>App Notification</h2>
              <label htmlFor="app-toggle" className="notification-toggle">
                <span>Enable App Notification</span>
                <Switch id="app-toggle" checked={appEnabled} onChange={handleAppToggle} disabled={saving} />
              </label>
            </div> */}

            {saving && <p className="saving-hint">Saving…</p>}
            {!!savedAt && !saving && !error && <p className="saved-hint">Saved ✓</p>}
            {error && <p className="error-hint">{error}</p>}
          </div>
        )}
      </div>

      {/* STYLES */}
      <style>{`
        .notification-options {
          max-width: 100%;
          overflow-x: hidden;
        }
        .notification-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 4px 0;
        }
        .toggle {
          position: relative;
          display: inline-flex;
          align-items: center;
          width: 60px;
          height: 30px;
          border-radius: 30px;
          border: 2px solid #FBEAD3;
          background: transparent;
          cursor: pointer;
          padding: 0;
          transition: background 0.2s, opacity 0.2s;
        }
        .toggle-disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .toggle-on {
          background: #FBEAD3;
        }
        .knob {
          position: absolute;
          top: 50%;
          left: 3px;
          width: 24px;
          height: 24px;
          background: #fff;
          border-radius: 50%;
          transition: transform 0.2s;
          transform: translateY(-50%);
        }
        .toggle-on .knob {
          transform: translateX(30px) translateY(-50%);
          background: #24252A;
        }
        .state-label {
          margin-left: 70px;
          font-size: 14px;
          min-width: 30px;
          text-align: left;
        }
        .saving-hint { color: #555; font-size: 0.9rem; }
        .saved-hint { color: #2e7d32; font-size: 0.9rem; }
        .error-hint { color: #c62828; font-size: 0.9rem; }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }
      `}</style>
    </div>
  );
};


// import { doc, getDoc, setDoc, query, where, getDocs, collection, onSnapshot } from 'firebase/firestore';
// import { db, auth } from '../config/firebase';
// import { useEffect, useRef, useState, useCallback } from 'react';
// import { useAuthState } from 'react-firebase-hooks/auth';
// import Modal from 'react-modal';
// import { useNavigate } from 'react-router-dom';

// export const NotificationsSettings = () => {
//   const [user] = useAuthState(auth);
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);
//   const [savedAt, setSavedAt] = useState(null);
//   const [emailEnabled, setEmailEnabled] = useState(true);
//   const [inBrowserEnabled, setInBrowserEnabled] = useState(true);
//   const [appEnabled, setAppEnabled] = useState(true);

//   const [error, setError] = useState('');
//   const [isPremium, setIsPremium] = useState(false);
//   const [showUpgradeModal, setShowUpgradeModal] = useState(false);
//   const [emailContent, setEmailContent] = useState(true);
//   const [emailTitle, setEmailTitle] = useState('');

//   const userDocRef = useRef(null);
//   const unsubRef = useRef(null);

//   const Switch = ({ id, checked, onChange, disabled }) => (
//     <button
//       id={id}
//       type="button"
//       role="switch"
//       aria-checked={checked}
//       aria-disabled={disabled}
//       disabled={disabled}
//       onClick={() => !disabled && onChange(!checked)}
//       className={`toggle ${checked ? 'toggle-on' : 'toggle-off'} ${disabled ? 'toggle-disabled' : ''}`}
//     >
//       <span className="knob" />
//       <span className="sr-only">{checked ? 'On' : 'Off'}</span>
//       <span className="state-label">{checked ? 'On' : 'Off'}</span>
//     </button>
//   );

//   useEffect(() => {
//     if (unsubRef.current) {
//       unsubRef.current();
//       unsubRef.current = null;
//     }
//     userDocRef.current = null;

//     const init = async () => {
//       try {
//         setLoading(true);
//         setError('');
//         setSavedAt(null);

//         if (!user?.uid) {
//           setError('You must be signed in to view notification settings.');
//           setLoading(false);
//           return;
//         }

//         const authUid = user.uid;
//         const idDocRef = doc(db, 'users', authUid);
//         const idDoc = await getDoc(idDocRef);

//         if (idDoc.exists()) {
//           if (idDoc.data()?.authUID !== authUid) {
//             await setDoc(idDocRef, { authUID: authUid }, { merge: true });
//           }
//           userDocRef.current = idDocRef;
//         } else {
//           const q = query(collection(db, 'users'), where('authUID', '==', authUid));
//           const snap = await getDocs(q);

//           if (!snap.empty) {
//             userDocRef.current = snap.docs[0].ref;
//           } else {
//             await setDoc(idDocRef, { authUID: authUid, emailNotifications: true }, { merge: true });
//             userDocRef.current = idDocRef;
//           }
//         }

//         unsubRef.current = onSnapshot(userDocRef.current, (ds) => {
//           const data = ds.data() || {};
//           setEmailEnabled(data.emailNotifications !== false);
//           setIsPremium(!!data.isPremium);
//           setEmailContent(data.emailContent !== false);
//           setEmailTitle(data.emailTitle || '');
//         });
//       } catch (e) {
//         console.error(e);
//         setError('Failed to load your settings.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     void init();

//     return () => {
//       if (unsubRef.current) {
//         unsubRef.current();
//         unsubRef.current = null;
//       }
//     };
//   }, [user]);

//   const persistPreference = useCallback(
//     async (preferences) => {
//       if (!user?.uid || !userDocRef.current) return;
//       setSaving(true);
//       setSavedAt(null);
//       setError('');

//       try {
//         await setDoc(
//           userDocRef.current,
//           {
//             authUID: user.uid,
//             ...preferences
//           },
//           { merge: true }
//         );
//         setSavedAt(new Date());
//       } catch (e) {
//         console.error(e);
//         setError('Could not save your preference. Please try again.');
//       } finally {
//         setSaving(false);
//       }
//     },
//     [user]
//   );

//   const handleEmailToggle = (val) => {
//     setEmailEnabled(val);
//     void persistPreference({ emailNotifications: val });
//   };

//   const handleInBrowserToggle = (val) => {
//     setInBrowserEnabled(val);
//     void persistPreference({ inBrowserNotifications: val });
//   };

//   const handleAppToggle = (val) => {
//     setAppEnabled(val);
//     void persistPreference({ appNotifications: val });
//   };

//   const handleEmailContentToggle = (val) => {
//     if (!isPremium) {
//       setShowUpgradeModal(true);
//       return;
//     }
//     setEmailContent(val);
//     void persistPreference({ emailContent: val });
//   };

//   const handleEmailTitleChange = (e) => {
//     if (!isPremium) {
//       setShowUpgradeModal(true);
//       return;
//     }
//     setEmailTitle(e.target.value);
//   };

//   const handleEmailTitleBlur = () => {
//     if (isPremium) {
//       void persistPreference({ emailTitle: emailTitle });
//     }
//   };

//   return (
//     <div className="main settings notifications">
//       <Modal
//         isOpen={showUpgradeModal}
//         onRequestClose={() => setShowUpgradeModal(false)}
//         contentLabel="Premium Feature"
//         className="upgrade-prompt"
//       >
//         <h2>Premium Feature</h2>
//         <p>This is a premium feature. Please upgrade to use it.</p>
//         <div className="actions">
//           <button onClick={() => setShowUpgradeModal(false)}>Maybe Later</button>
//           <button onClick={() => navigate('/pricing')}>Upgrade</button>
//         </div>
//       </Modal>
//       <div>
//         <h1>Notifications</h1>
//         <p>Control How you recive alerts from the platform</p>

//         {loading ? (
//           <p>Loading…</p>
//         ) : (
//           <div>
//             <div className="notification-options">
//               <h1>Email Notification</h1>
//               <label htmlFor="email-toggle" className="notification-toggle">
//                 <span>Email notifications</span>
//                 <Switch id="email-toggle" checked={emailEnabled} onChange={handleEmailToggle} disabled={saving} />
//               </label>

//               <label htmlFor="email-content-toggle" className="notification-toggle">
//                 <span>Include message content in email</span>
//                 <Switch id="email-content-toggle" checked={emailContent} onChange={handleEmailContentToggle} disabled={saving || !isPremium} />
//               </label>

//               <div className="notification-toggle">
//                 <label htmlFor="email-title">Custom email title</label>
//                 <input
//                   type="text"
//                   id="email-title"
//                   value={emailTitle}
//                   onChange={handleEmailTitleChange}
//                   onBlur={handleEmailTitleBlur}
//                   disabled={saving || !isPremium}
//                   placeholder="New message from..."
//                 />
//               </div>
//             </div>
//             {/* inBrower Notification */}
//             <div className="notification-options">
//               <h2 style={{ fontSize: '24px' }}>In-Browser Notification</h2>
//               <label htmlFor="in-browser-toggle" className="notification-toggle">
//                 <span>Enable In-Browser Notification</span>
//                 <Switch id="in-browser-toggle" checked={inBrowserEnabled} onChange={handleInBrowserToggle} disabled={saving} />
//               </label>
//             </div>
//             {/* App Notification */}
//             <div className="notification-options">
//               <h2 style={{ fontSize: '24px' }}>App Notification</h2>
//               <label htmlFor="app-toggle" className="notification-toggle">
//                 <span>Enable App Notification</span>
//                 <Switch id="app-toggle" checked={appEnabled} onChange={handleAppToggle} disabled={saving} />
//               </label>
//             </div> {saving && <p className="saving-hint">Saving…</p>}
//             {!!savedAt && !saving && !error && <p className="saved-hint">Saved ✓</p>}
//             {error && <p className="error-hint">{error}</p>}
//           </div>
//         )}
//       </div>

//       {/* --- Styles for the toggle --- */}
//       <style>{`
//         .notification-options {
//           max-width: 100%;
//           overflow-x: hidden;
//         }
//         .notification-toggle {
//           display: flex;
//           align-items: center;
//           justify-content: space-between;
//           gap: 12px;
//           padding: 4px 0;
//         }
//         .toggle {
//           position: relative;
//           display: inline-flex;
//           align-items: center;
//           width: 60px;
//           height: 30px;
//           border-radius: 30px;
//           border: 2px solid #FBEAD3;
//           background: transparent;
//           cursor: pointer;
//           padding: 0;
//           transition: background 0.2s, opacity 0.2s;
//         }
//         .toggle-disabled {
//           opacity: 0.6;
//           cursor: not-allowed;
//         }
//         .toggle-on {
//           background: #FBEAD3;
//         }
//         .knob {
//           position: absolute;
//           top: 50%;
//           left: 3px;
//           width: 24px;
//           height: 24px;
//           background: #fff;
//           border-radius: 50%;
//           transition: transform 0.2s;
//           transform: translateY(-50%);
//         }
//         .toggle-on .knob {
//           transform: translateX(30px) translateY(-50%);   /* slide right when ON */
//           background: #24252A;
//         }
//         .state-label {
//           margin-left: 70px;
//           font-size: 14px;
//           min-width: 30px;
//           text-align: left;
//         }
//         .saving-hint { color: #555; font-size: 0.9rem; }
//         .saved-hint { color: #2e7d32; font-size: 0.9rem; }
//         .error-hint { color: #c62828; font-size: 0.9rem; }
//         .sr-only {
//           position: absolute;
//           width: 1px;
//           height: 1px;
//           padding: 0;
//           margin: -1px;
//           overflow: hidden;
//           clip: rect(0, 0, 0, 0);
//           border: 0;
//         }
//       `}</style>
//     </div>
//   );
// };
